import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { 
  Plus, 
  Search, 
  Upload, 
  Edit, 
  Trash2, 
  Loader2,
  ChevronDown,
  ChevronRight,
  Server,
  Folder,
  Wrench,
  Settings,
  X
} from "lucide-react"
import { Button } from "@/shared/components/ui/button"
import { Card, CardContent } from "@/shared/components/ui/card"
import { Input } from "@/shared/components/ui/input"
import { Label } from "@/shared/components/ui/label"
import { Badge } from "@/shared/components/ui/badge"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shared/components/ui/collapsible"
import { ToolApiService } from "@/modules/agent/services/tool-api"
import { useToast } from "@/shared/hooks/useToast"
import { ToastContainer } from "@/shared/components/ui/toast"
import type { ToolListItem, ToolCategory, CreateToolRequest, ToolParameterProperty } from "@/modules/agent/types/tool"
import { TOOL_CATEGORY_NAMES } from "@/modules/agent/types/tool"

/**
 * 工具分组类型
 */
interface ToolGroup {
  name: string
  type: 'mcp' | 'category' // mcp = MCP Server 分组, category = 普通分类
  tools: ToolListItem[]
}

/**
 * 工具管理页面
 * 系统级工具管理（添加、编辑、删除、列表、导入）
 */
export default function ToolManagement() {
  const navigate = useNavigate()
  const { toasts, showError, showSuccess, removeToast } = useToast()

  const [tools, setTools] = useState<ToolListItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedCategory, setSelectedCategory] = useState<ToolCategory | "all">("all")
  
  // 对话框状态
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false)
  
  // 上下文参数设置状态
  const [contextParams, setContextParams] = useState<string[]>([])
  const [newContextParam, setNewContextParam] = useState('')
  const [isLoadingSettings, setIsLoadingSettings] = useState(false)
  const [isSavingSettings, setIsSavingSettings] = useState(false)
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [toolToDelete, setToolToDelete] = useState<ToolListItem | null>(null)
  
  // 导入状态
  const [isImporting, setIsImporting] = useState(false)
  
  // 展开状态
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
  const [expandedTools, setExpandedTools] = useState<Set<string>>(new Set())
  
  // 表单数据
  const [newTool, setNewTool] = useState<CreateToolRequest>({
    name: "",
    description: "",
    category: "通用工具",
    mcp_server: {
      type: "http",
      url: "",
      auth: {
        type: "none",
      },
    },
    parameters: {
      input: {
        type: "object",
        properties: {},
        required: [],
      },
      output: {
        type: "object",
        properties: {},
      },
    },
    tags: [],
  })
  const [importFile, setImportFile] = useState<File | null>(null)
  const [importCategory, setImportCategory] = useState<ToolCategory>("其他")
  const [importScope, setImportScope] = useState<"internal" | "external">("external")

  // 加载工具列表
  const loadTools = async () => {
    setIsLoading(true)
    try {
      const response = await ToolApiService.getTools({
        category: selectedCategory !== "all" ? selectedCategory : undefined,
        search: searchQuery || undefined,
      })
      setTools(response.data)
      // 默认展开所有分组
      const groups = groupTools(response.data)
      setExpandedGroups(new Set(groups.map(g => g.name)))
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '加载工具列表失败'
      showError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadTools()
  }, [selectedCategory, searchQuery])

  // 加载上下文参数配置
  const loadContextParams = async () => {
    setIsLoadingSettings(true)
    try {
      const response = await ToolApiService.getContextParams()
      setContextParams(response.data.params || [])
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '加载设置失败'
      showError(errorMessage)
    } finally {
      setIsLoadingSettings(false)
    }
  }

  // 保存上下文参数配置
  const handleSaveContextParams = async () => {
    setIsSavingSettings(true)
    try {
      await ToolApiService.updateContextParams(contextParams)
      showSuccess('上下文参数配置已保存')
      setIsSettingsDialogOpen(false)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '保存设置失败'
      showError(errorMessage)
    } finally {
      setIsSavingSettings(false)
    }
  }

  // 添加上下文参数（支持顿号、逗号、空格分隔的多个参数）
  const handleAddContextParam = () => {
    const input = newContextParam.trim()
    if (!input) return
    
    // 支持多种分隔符：顿号、逗号、空格
    const params = input
      .split(/[、,，\s]+/)
      .map(p => p.trim())
      .filter(p => p && !contextParams.includes(p))
    
    if (params.length > 0) {
      setContextParams([...contextParams, ...params])
      setNewContextParam('')
    }
  }

  // 移除上下文参数
  const handleRemoveContextParam = (param: string) => {
    setContextParams(contextParams.filter(p => p !== param))
  }

  // 打开设置对话框时加载配置
  const handleOpenSettings = () => {
    setIsSettingsDialogOpen(true)
    loadContextParams()
  }

  // 按 server_name 或 category 分组工具
  const groupTools = (toolList: ToolListItem[]): ToolGroup[] => {
    const mcpGroups = new Map<string, ToolListItem[]>()
    const categoryGroups = new Map<string, ToolListItem[]>()
    
    toolList.forEach(tool => {
      if (tool.server_name) {
        // MCP 工具按 server_name 分组
        const existing = mcpGroups.get(tool.server_name) || []
        existing.push(tool)
        mcpGroups.set(tool.server_name, existing)
      } else {
        // 普通工具按 category 分组
        const category = tool.category || '其他'
        const existing = categoryGroups.get(category) || []
        existing.push(tool)
        categoryGroups.set(category, existing)
      }
    })
    
    const groups: ToolGroup[] = []
    
    // 先添加 MCP Server 分组
    mcpGroups.forEach((tools, serverName) => {
      groups.push({
        name: serverName,
        type: 'mcp',
        tools: tools.sort((a, b) => a.name.localeCompare(b.name))
      })
    })
    
    // 再添加普通分类分组
    categoryGroups.forEach((tools, category) => {
      groups.push({
        name: category,
        type: 'category',
        tools: tools.sort((a, b) => a.name.localeCompare(b.name))
      })
    })
    
    return groups.sort((a, b) => {
      // MCP 分组排在前面
      if (a.type !== b.type) return a.type === 'mcp' ? -1 : 1
      return a.name.localeCompare(b.name)
    })
  }

  const toolGroups = groupTools(tools)

  // 切换分组展开状态
  const toggleGroup = (groupName: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev)
      if (next.has(groupName)) {
        next.delete(groupName)
      } else {
        next.add(groupName)
      }
      return next
    })
  }

  // 切换工具详情展开状态
  const toggleTool = (toolId: string) => {
    setExpandedTools(prev => {
      const next = new Set(prev)
      if (next.has(toolId)) {
        next.delete(toolId)
      } else {
        next.add(toolId)
      }
      return next
    })
  }

  // 创建工具
  const handleCreateTool = async () => {
    if (!newTool.name.trim()) {
      showError("请输入工具名称")
      return
    }

    try {
      await ToolApiService.createTool(newTool)
      showSuccess("工具创建成功")
      setIsCreateDialogOpen(false)
      setNewTool({
        name: "",
        description: "",
        category: "通用工具",
        mcp_server: {
          type: "http",
          url: "",
          auth: {
            type: "none",
          },
        },
        parameters: {
          input: {
            type: "object",
            properties: {},
            required: [],
          },
          output: {
            type: "object",
            properties: {},
          },
        },
        tags: [],
      })
      loadTools()
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '创建工具失败'
      showError(errorMessage)
    }
  }

  // 导入工具
  const handleImportTools = async () => {
    if (!importFile) {
      showError("请选择要导入的配置文件")
      return
    }

    setIsImporting(true)
    try {
      // 读取文件内容并解析 JSON
      const fileContent = await importFile.text()
      let config: Record<string, unknown>
      
      try {
        config = JSON.parse(fileContent)
      } catch {
        showError("配置文件格式错误，请确保是有效的 JSON 文件")
        setIsImporting(false)
        return
      }
      
      // 传递用户选择的分类和 scope
      const response = await ToolApiService.importTools(config, importCategory, importScope)
      showSuccess(`✅ 成功导入 ${response.data.success_count} 个工具`)
      setIsImportDialogOpen(false)
      setImportFile(null)
      setImportCategory("其他")
      setImportScope("external")
      loadTools()
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '导入工具失败'
      showError(errorMessage)
    } finally {
      setIsImporting(false)
    }
  }

  // 删除工具
  const handleDeleteTool = async () => {
    if (!toolToDelete) return

    try {
      await ToolApiService.deleteTool(toolToDelete.id)
      showSuccess("工具删除成功")
      setIsDeleteDialogOpen(false)
      setToolToDelete(null)
      loadTools()
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '删除工具失败'
      showError(errorMessage)
    }
  }

  // 渲染参数详情
  const renderParameters = (tool: ToolListItem) => {
    const params = tool.parameters
    if (!params?.properties || Object.keys(params.properties).length === 0) {
      return <p className="text-sm text-slate-400 italic">无参数</p>
    }

    const required = params.required || []
    
    return (
      <div className="space-y-2">
        <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">参数列表</p>
        <div className="space-y-1.5">
          {Object.entries(params.properties).map(([name, prop]) => {
            const typedProp = prop as ToolParameterProperty
            const isRequired = required.includes(name)
            return (
              <div 
                key={name} 
                className="flex items-start gap-3 text-sm bg-slate-50 dark:bg-slate-800/50 rounded px-3 py-2"
              >
                <code className="font-mono text-blue-600 dark:text-blue-400 shrink-0">
                  {name}
                </code>
                <Badge variant="outline" className="text-[10px] shrink-0">
                  {typedProp.type || 'any'}
                </Badge>
                {isRequired && (
                  <Badge variant="destructive" className="text-[10px] shrink-0">
                    必填
                  </Badge>
                )}
                {typedProp.description && (
                  <span className="text-slate-500 dark:text-slate-400 text-xs">
                    {typedProp.description}
                  </span>
                )}
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* Toast 容器 */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      {/* 顶部导航栏 */}
      <header className="border-b bg-white dark:bg-slate-900 sticky top-0 z-10">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/")}
            >
              返回
            </Button>
            <h1 className="text-xl font-bold">工具管理</h1>
          </div>
        </div>
      </header>

      {/* 主内容区 */}
      <main className="container mx-auto px-4 py-8">
        {/* 工具栏 */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4 flex-1">
            {/* 搜索框 */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="搜索工具名称或描述..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* 分类筛选 */}
            <Select
              value={selectedCategory}
              onValueChange={(v) => setSelectedCategory(v as ToolCategory | "all")}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="选择分类" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部分类</SelectItem>
                {Object.entries(TOOL_CATEGORY_NAMES).map(([key, name]) => (
                  <SelectItem key={key} value={key}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            {/* 设置按钮 */}
            <Button
              variant="outline"
              size="icon"
              onClick={handleOpenSettings}
              title="上下文参数配置"
            >
              <Settings className="w-4 h-4" />
            </Button>

            {/* 导入工具按钮 */}
            <Button
              variant="outline"
              onClick={() => setIsImportDialogOpen(true)}
            >
              <Upload className="w-4 h-4 mr-2" />
              导入工具
            </Button>

            {/* 创建工具按钮 */}
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              创建工具
            </Button>
          </div>
        </div>

        {/* 工具列表 */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : tools.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Wrench className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500">暂无工具</p>
              <p className="text-sm text-slate-400 mt-1">点击上方按钮创建或导入工具</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {toolGroups.map((group) => (
              <Collapsible
                key={group.name}
                open={expandedGroups.has(group.name)}
                onOpenChange={() => toggleGroup(group.name)}
              >
                <Card className="overflow-hidden">
                  {/* 分组标题 */}
                  <CollapsibleTrigger asChild>
                    <button className="w-full flex items-center justify-between p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors text-left">
                      <div className="flex items-center gap-3">
                        {expandedGroups.has(group.name) ? (
                          <ChevronDown className="h-4 w-4 text-slate-400" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-slate-400" />
                        )}
                        {group.type === 'mcp' ? (
                          <Server className="h-5 w-5 text-blue-500" />
                        ) : (
                          <Folder className="h-5 w-5 text-amber-500" />
                        )}
                        <span className="font-medium">
                          {group.name}
                          {group.type === 'mcp' && (
                            <span className="text-xs text-slate-400 ml-2">(MCP Server)</span>
                          )}
                        </span>
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {group.tools.length} 个工具
                      </Badge>
                    </button>
                  </CollapsibleTrigger>

                  {/* 工具列表 */}
                  <CollapsibleContent>
                    <div className="border-t divide-y">
                      {group.tools.map((tool) => (
                        <Collapsible
                          key={tool.id}
                          open={expandedTools.has(tool.id)}
                          onOpenChange={() => toggleTool(tool.id)}
                        >
                          {/* 工具行 */}
                          <div className="flex items-center group">
                            <CollapsibleTrigger asChild>
                              <button className="flex-1 flex items-center gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors text-left min-w-0">
                                <span className="text-slate-300 w-6 flex justify-center shrink-0">
                                  {expandedTools.has(tool.id) ? (
                                    <ChevronDown className="h-3 w-3" />
                                  ) : (
                                    <ChevronRight className="h-3 w-3" />
                                  )}
                                </span>
                                <Wrench className="h-4 w-4 text-slate-400 shrink-0" />
                                <code className="font-mono text-sm text-slate-700 dark:text-slate-300 shrink-0">
                                  {tool.name}
                                </code>
                                <span className="text-sm text-slate-500 truncate">
                                  {tool.description}
                                </span>
                              </button>
                            </CollapsibleTrigger>
                            
                            {/* 操作按钮 */}
                            <div className="flex items-center gap-1 pr-4 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  showError("编辑功能待实现")
                                }}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                onClick={async (e) => {
                                  e.stopPropagation()
                                  try {
                                    const usageResponse = await ToolApiService.getToolUsage(tool.id)
                                    const usageCount = usageResponse.data.items.reduce((sum, usage) => sum + usage.use_count, 0)
                                    if (usageCount > 0) {
                                      showError(`该工具正在被使用（使用次数: ${usageCount}），无法删除`)
                                      return
                                    }
                                    setToolToDelete(tool)
                                    setIsDeleteDialogOpen(true)
                                  } catch {
                                    setToolToDelete(tool)
                                    setIsDeleteDialogOpen(true)
                                  }
                                }}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>

                          {/* 工具详情（展开后显示） */}
                          <CollapsibleContent>
                            <div className="px-4 pb-4 pl-14 space-y-3">
                              {/* 描述 */}
                              {tool.description && (
                                <div className="text-sm text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/30 rounded-lg p-3">
                                  {tool.description}
                                </div>
                              )}
                              
                              {/* 参数 */}
                              {renderParameters(tool)}
                              
                              {/* 标签 */}
                              {tool.tags && tool.tags.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 pt-2">
                                  {tool.tags.map((tag) => (
                                    <Badge key={tag} variant="outline" className="text-xs">
                                      {tag}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      ))}
                    </div>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            ))}
          </div>
        )}

        {/* 创建工具对话框 */}
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>创建工具</DialogTitle>
              <DialogDescription>
                创建一个新的MCP工具
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>工具名称 *</Label>
                <Input
                  value={newTool.name}
                  onChange={(e) =>
                    setNewTool({ ...newTool, name: e.target.value })
                  }
                  placeholder="输入工具名称"
                />
              </div>
              <div className="space-y-2">
                <Label>工具描述</Label>
                <Input
                  value={newTool.description}
                  onChange={(e) =>
                    setNewTool({ ...newTool, description: e.target.value })
                  }
                  placeholder="输入工具描述"
                />
              </div>
              <div className="space-y-2">
                <Label>工具分类</Label>
                <Select
                  value={newTool.category}
                  onValueChange={(v) =>
                    setNewTool({ ...newTool, category: v as ToolCategory })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(TOOL_CATEGORY_NAMES).map(([key, name]) => (
                      <SelectItem key={key} value={key}>
                        {name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>MCP服务器类型</Label>
                  <Select
                    value={newTool.mcp_server.type}
                    onValueChange={(v) =>
                      setNewTool({
                        ...newTool,
                        mcp_server: { ...newTool.mcp_server, type: v as 'stdio' | 'sse' | 'http' },
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="stdio">Stdio</SelectItem>
                      <SelectItem value="sse">SSE</SelectItem>
                      <SelectItem value="http">HTTP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>MCP服务器URL</Label>
                  <Input
                    value={newTool.mcp_server.url}
                    onChange={(e) =>
                      setNewTool({
                        ...newTool,
                        mcp_server: { ...newTool.mcp_server, url: e.target.value },
                      })
                    }
                    placeholder="输入MCP服务器地址"
                  />
                </div>
                <div className="space-y-2">
                  <Label>认证方式</Label>
                  <Select
                    value={newTool.mcp_server.auth?.type || 'none'}
                    onValueChange={(v) =>
                      setNewTool({
                        ...newTool,
                        mcp_server: {
                          ...newTool.mcp_server,
                          auth: { type: v as 'bearer' | 'api_key' | 'none', value: '' },
                        },
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">无认证</SelectItem>
                      <SelectItem value="bearer">Bearer Token</SelectItem>
                      <SelectItem value="api_key">API Key</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {newTool.mcp_server.auth?.type !== 'none' && (
                  <div className="space-y-2">
                    <Label>认证值</Label>
                    <Input
                      type="password"
                      value={newTool.mcp_server.auth?.value || ''}
                      onChange={(e) =>
                        setNewTool({
                          ...newTool,
                          mcp_server: {
                            ...newTool.mcp_server,
                            auth: { ...newTool.mcp_server.auth!, value: e.target.value },
                          },
                        })
                      }
                      placeholder="输入认证令牌或API Key"
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <Label>工具参数（JSON格式）</Label>
                  <textarea
                    className="w-full min-h-[200px] p-2 border rounded-lg font-mono text-sm"
                    value={JSON.stringify(newTool.parameters, null, 2)}
                    onChange={(e) => {
                      try {
                        const params = JSON.parse(e.target.value)
                        setNewTool({ ...newTool, parameters: params })
                      } catch {
                        // 忽略JSON解析错误，允许用户继续编辑
                      }
                    }}
                    placeholder='{"input": {"type": "object", "properties": {...}}, "output": {...}}'
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                取消
              </Button>
              <Button onClick={handleCreateTool}>创建</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* 导入工具对话框 */}
        <Dialog open={isImportDialogOpen} onOpenChange={(open) => {
          if (!isImporting) {
            setIsImportDialogOpen(open)
            if (!open) {
              setImportFile(null)
              setImportCategory("其他")
            }
          }
        }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>导入工具</DialogTitle>
              <DialogDescription>
                上传MCP配置文件（JSON格式）批量导入工具
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>选择配置文件</Label>
                <Input
                  type="file"
                  accept=".json"
                  disabled={isImporting}
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) {
                      setImportFile(file)
                    }
                  }}
                />
                {importFile && (
                  <p className="text-sm text-slate-500">
                    已选择: {importFile.name}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>工具分类</Label>
                <Select
                  value={importCategory}
                  onValueChange={(v) => setImportCategory(v as ToolCategory)}
                  disabled={isImporting}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="选择分类" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(TOOL_CATEGORY_NAMES).map(([key, name]) => (
                      <SelectItem key={key} value={key}>
                        {name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  导入的所有工具将归入此分类
                </p>
              </div>
              <div className="space-y-2">
                <Label>作用域（Scope）</Label>
                <Select
                  value={importScope}
                  onValueChange={(v) => setImportScope(v as "internal" | "external")}
                  disabled={isImporting}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="选择作用域" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="external">
                      <div className="flex flex-col">
                        <span>External（默认）</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="internal">
                      <div className="flex flex-col">
                        <span>Internal</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  {importScope === 'internal' 
                    ? '系统自动注入上下文参数（device_id、user_id 等），LLM 不可见'
                    : '参数由 LLM 生成，不注入上下文'
                  }
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setIsImportDialogOpen(false)}
                disabled={isImporting}
              >
                取消
              </Button>
              <Button 
                onClick={handleImportTools} 
                disabled={!importFile || isImporting}
              >
                {isImporting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    正在连接 MCP 服务器...
                  </>
                ) : (
                  '导入'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* 删除确认对话框 */}
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>确认删除</AlertDialogTitle>
              <AlertDialogDescription>
                确定要删除工具 "{toolToDelete?.name}" 吗？此操作不可恢复。
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>取消</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteTool}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                删除
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* 上下文参数设置对话框 */}
        <Dialog open={isSettingsDialogOpen} onOpenChange={setIsSettingsDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                上下文参数配置
              </DialogTitle>
              <DialogDescription>
                这些参数由系统自动注入到工具调用中，LLM 不可见
              </DialogDescription>
            </DialogHeader>

            {isLoadingSettings ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              <div className="space-y-4 py-4">
                {/* 已配置的参数列表 */}
                <div className="space-y-2">
                  <Label>已配置的上下文参数</Label>
                  <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-lg min-h-[60px]">
                    {contextParams.length === 0 ? (
                      <p className="text-sm text-slate-400 italic">暂无配置</p>
                    ) : (
                      contextParams.map((param) => (
                        <Badge 
                          key={param} 
                          variant="secondary"
                          className="flex items-center gap-1 px-2 py-1"
                        >
                          <code className="text-xs">{param}</code>
                          <button
                            onClick={() => handleRemoveContextParam(param)}
                            className="ml-1 hover:text-destructive transition-colors"
                            title="移除"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))
                    )}
                  </div>
                </div>

                {/* 添加新参数 */}
                <div className="space-y-2">
                  <Label>添加参数</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newContextParam}
                      onChange={(e) => setNewContextParam(e.target.value)}
                      placeholder="输入参数名，多个用逗号或空格分隔"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          handleAddContextParam()
                        }
                      }}
                    />
                    <Button 
                      variant="outline" 
                      onClick={handleAddContextParam}
                      disabled={!newContextParam.trim()}
                    >
                      添加
                    </Button>
                  </div>
                </div>

                {/* 说明 */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-700">
                    <strong>说明：</strong>
                  </p>
                  <ul className="text-xs text-blue-600 mt-1 space-y-1 list-disc list-inside">
                    <li>上下文参数会自动注入到所有定义了该参数的工具中</li>
                    <li>这些参数对 LLM 不可见，不会出现在提示词中</li>
                    <li>常用参数：device_id、user_id、external_user_id、session_id、project_id</li>
                  </ul>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setIsSettingsDialogOpen(false)}
                disabled={isSavingSettings}
              >
                取消
              </Button>
              <Button 
                onClick={handleSaveContextParams}
                disabled={isLoadingSettings || isSavingSettings}
              >
                {isSavingSettings ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    保存中...
                  </>
                ) : (
                  '保存'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  )
}
